/**
 * playerEventQueue — the small, bounded on-device queue for player_events
 * batches that could not be sent.
 *
 * WHY IT EXISTS. usePlayerLog used to DROP its buffer whenever the network was
 * unreachable, and said so in its own header. That was the right call while
 * player_events was diagnostics: a lost row cost a line on a chart. Since jobs
 * #655/#657 (2026-09-22) the same rail carries the ADAPTATION SIGNAL for Script
 * mode — `script_answer`, `script_distinction_done` — so an offline session
 * taught the population nothing at all. Tom's ruling, via RBF, 2026-09-22
 * 13:15Z: keep a small on-device queue and replay it. This is that queue.
 *
 * WHY localStorage AND NOT IndexedDB. The moment we most need to persist is
 * the moment the page may be evicted — a hidden tab on iOS, an unload. A
 * localStorage write is SYNCHRONOUS and has completed before the handler
 * returns; an IndexedDB transaction opened in a visibilitychange handler is
 * not guaranteed to commit before the page is killed, which is the one failure
 * this queue exists to prevent. It is also simpler (no schema, no versioning,
 * no open/upgrade dance) and the cap below sits far inside the ~5 MB origin
 * quota. IndexedDB stays where it earns its complexity — AudioCache, where the
 * payloads are megabytes.
 *
 * RETAIN UNTIL ACKED (job #671, correcting #666). The queue has NO drain-all
 * primitive, deliberately. #666 shipped a `takeQueue()` that removed every
 * batch from storage BEFORE the first request was made; a page evicted or
 * reloaded mid-replay lost every batch that had not yet been posted. So the
 * only way out of this queue is: read it with `peekQueue()`, post ONE batch,
 * and call `removeBatch(batch.id)` once that post came back ok. Every batch
 * carries a stable `id` for exactly that, healed onto any batch written by the
 * older code. A batch is removed because it was ACCEPTED, never because it was
 * about to be tried.
 *
 * BOUNDED. MAX_QUEUED_EVENTS / MAX_QUEUED_BYTES, whichever bites first. PAST
 * THE CAP THE OLDEST BATCHES ARE DROPPED, not the newest: recent adaptation
 * signal is worth more than stale, and a learner who has been offline for days
 * would otherwise be unable to record anything new. When one batch is all that
 * is left and it is still over, its OLDEST EVENTS go, one at a time, until it
 * fits — so the cap is a real ceiling rather than a hint. Every drop is counted
 * in `dropped` and re-reported once as a `telemetry_queue_dropped` event, and
 * the counter is cleared only once that report has been ACCEPTED
 * (`clearDropped`), so a failed report does not erase the evidence of loss.
 *
 * ORDER AND TIMESTAMPS. Batches replay oldest-first and events keep the
 * `occurred_at` they were stamped with at the instant they happened. Nothing is
 * ever re-stamped — a replayed row is a row about the past, and the server
 * takes the client's occurred_at as given.
 *
 * IDENTITY. A batch remembers the learner it was recorded under
 * (`recordedLearnerId`), stamped on each event AT THE MOMENT IT WAS RECORDED
 * and not at flush time, so a learner switch between recording and flushing
 * cannot re-file one learner's events under another. A buffer spanning two
 * identities is split into one batch per identity before it is queued. Replay
 * attaches a bearer only when the batch's identity is the identity signed in
 * now — see usePlayerLog's replay path. The bearer token is deliberately NOT
 * persisted: it expires within the hour, so it would be useless on replay, and
 * a token at rest is a liability. Events recorded while an admin is viewing-as
 * never reach this queue because they are never buffered in the first place.
 */

export interface QueuedEvent {
  event_type: string
  payload?: Record<string, unknown> | null
  course_code?: string | null
  session_id?: string
  occurred_at: string
  client_version?: string | null
  /** Per-event idempotence key — see usePlayerLog / api/player-events.ts. */
  client_event_id?: string
}

export interface QueuedBatch {
  /**
   * Stable identity of this batch in the queue. Assigned at enqueue, healed
   * onto batches written by the pre-#671 code, and the ONLY handle
   * `removeBatch` accepts — so a batch can be removed after its post was
   * acked rather than before it was attempted.
   */
  id?: string
  events: QueuedEvent[]
  /** 'web' | 'webview', as declared when the batch was recorded. */
  appShell?: string
  /** The play-as-class claim the batch was recorded with, if any. */
  actingLearnerId?: string | null
  /**
   * The learner this batch belongs to (`learners.id`, or `guest-<uuid>`, or
   * null for an unidentified session), as stamped when the events were
   * RECORDED. Replay refuses to attach a bearer that does not match it.
   */
  recordedLearnerId?: string | null
}

interface QueueFile {
  v: 1
  batches: QueuedBatch[]
  /** Events lost to the cap since the last time we reported the loss. */
  dropped: number
}

export const QUEUE_KEY = 'ssi-player-events-queue-v1'

/**
 * THE CAP. 2,000 events or 512 KB, whichever bites first.
 *
 * Heavy Script use emits roughly one `script_answer` per answered item, on the
 * order of 10-15 items a minute, so 2,000 events is about two to three hours of
 * solid offline practice — the "a few hours, not a week" the brief asks for —
 * and at the ~250 B a stamped event serializes to, the byte cap bites at about
 * the same place. Sized to stay a small fraction of the ~5 MB localStorage
 * origin quota the rest of the app also lives in.
 */
export const MAX_QUEUED_EVENTS = 2000
export const MAX_QUEUED_BYTES = 512 * 1024

/**
 * Server's own per-request limit (api/player-events.ts MAX_BATCH). A buffer
 * that filled up while offline is chunked to this on the way in, so a replayed
 * batch is never refused 400 for being too large.
 */
export const MAX_EVENTS_PER_BATCH = 50

function storage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null // Safari private mode, disabled storage — queue simply is not available
  }
}

function emptyFile(): QueueFile {
  return { v: 1, batches: [], dropped: 0 }
}

function genBatchId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch { /* fall through */ }
  return `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * BYTES, not UTF-16 code units. #666 measured `JSON.stringify(x).length`,
 * which counts code units: a queue of non-Latin payloads — Chinese script
 * items, Arabic letters, the very content this queue exists for — sat up to
 * three times over the cap it claimed to enforce. TextEncoder is present in
 * every browser that has the rest of this app; where it is not, the ×3
 * worst case for UTF-8 keeps us under the cap rather than over it.
 */
export function byteLength(s: string): number {
  try {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length
  } catch { /* fall through */ }
  return s.length * 3
}

function fileBytes(batches: QueuedBatch[]): number {
  return byteLength(JSON.stringify(batches))
}

export function readQueue(): QueueFile {
  const s = storage()
  if (!s) return emptyFile()
  try {
    const raw = s.getItem(QUEUE_KEY)
    if (!raw) return emptyFile()
    const parsed = JSON.parse(raw) as QueueFile
    if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.batches)) return emptyFile()
    // HEAL. A batch written by the pre-#671 code carries no id, and without
    // one it could never be removed by name. Assign and write back once, so a
    // learner's queued events survive the upgrade instead of being discarded.
    // A malformed batch must not poison event counting or block new enqueues.
    // Keep the readable batches, including legacy batches that need an id.
    const readable = parsed.batches.filter((b) => b && Array.isArray(b.events))
    let healed = readable.length !== parsed.batches.length
    const batches = readable.map((b) => {
      if (b && typeof b.id === 'string' && b.id) return b
      healed = true
      return { ...b, id: genBatchId() }
    })
    const file: QueueFile = { v: 1, batches, dropped: Number(parsed.dropped) || 0 }
    if (healed) writeQueue(file)
    return file
  } catch {
    return emptyFile()
  }
}

function writeQueue(file: QueueFile): void {
  const s = storage()
  if (!s) return
  try {
    if (file.batches.length === 0 && file.dropped === 0) {
      s.removeItem(QUEUE_KEY)
      return
    }
    s.setItem(QUEUE_KEY, JSON.stringify(file))
  } catch {
    // Quota exceeded despite our own cap (another key grew). Drop the oldest
    // half and try once; if that fails too, give the slot up entirely rather
    // than throw inside a telemetry path.
    try {
      const half = file.batches.slice(Math.floor(file.batches.length / 2))
      const lost = file.batches.slice(0, file.batches.length - half.length)
      s.setItem(QUEUE_KEY, JSON.stringify({
        v: 1,
        batches: half,
        dropped: file.dropped + lost.reduce((n, b) => n + b.events.length, 0),
      }))
    } catch {
      try { s.removeItem(QUEUE_KEY) } catch { /* nothing more to do */ }
    }
  }
}

function countEvents(batches: QueuedBatch[]): number {
  return batches.reduce((n, b) => n + b.events.length, 0)
}

/**
 * Trim to the cap by DROPPING THE OLDEST BATCHES FIRST, then — when one batch
 * is all that remains and it is still over — by dropping that batch's OLDEST
 * EVENTS one at a time. Returns the trimmed list and how many events were lost.
 */
function enforceCap(batches: QueuedBatch[]): { batches: QueuedBatch[]; lost: number } {
  let kept = batches
  let lost = 0
  while (kept.length > 1 && countEvents(kept) > MAX_QUEUED_EVENTS) {
    lost += kept[0].events.length
    kept = kept.slice(1)
  }
  while (kept.length > 1 && fileBytes(kept) > MAX_QUEUED_BYTES) {
    lost += kept[0].events.length
    kept = kept.slice(1)
  }
  if (kept.length === 1) {
    // Same rule one level down: oldest events go.
    if (kept[0].events.length > MAX_QUEUED_EVENTS) {
      const over = kept[0].events.length - MAX_QUEUED_EVENTS
      lost += over
      kept = [{ ...kept[0], events: kept[0].events.slice(over) }]
    }
    if (fileBytes(kept) > MAX_QUEUED_BYTES) {
      let events = kept[0].events
      while (events.length > 0 && fileBytes([{ ...kept[0], events }]) > MAX_QUEUED_BYTES) {
        events = events.slice(1)
        lost += 1
      }
      // A single event bigger than the whole cap cannot be kept at all.
      kept = events.length > 0 ? [{ ...kept[0], events }] : []
    }
  }
  return { batches: kept, lost }
}

/**
 * Persist a batch that could not be sent. Chunked to MAX_EVENTS_PER_BATCH,
 * appended in order, trimmed to the cap.
 */
export function enqueueBatch(batch: QueuedBatch): void {
  if (!batch.events || batch.events.length === 0) return
  const file = readQueue()
  for (let i = 0; i < batch.events.length; i += MAX_EVENTS_PER_BATCH) {
    file.batches.push({
      ...batch,
      id: genBatchId(),
      events: batch.events.slice(i, i + MAX_EVENTS_PER_BATCH),
    })
  }
  const { batches, lost } = enforceCap(file.batches)
  writeQueue({ v: 1, batches, dropped: file.dropped + lost })
}

/** Every queued batch, oldest first. Reading never removes anything. */
export function peekQueue(): QueuedBatch[] {
  return readQueue().batches
}

/**
 * Remove ONE batch, by the id it was queued under, because its post came back
 * ok. Everything else is left exactly where it was — which is what makes an
 * eviction mid-replay cost nothing. Returns whether the batch was there.
 */
export function removeBatch(id: string | undefined): boolean {
  if (!id) return false
  const file = readQueue()
  const idx = file.batches.findIndex((b) => b.id === id)
  if (idx === -1) return false
  const batches = file.batches.slice(0, idx).concat(file.batches.slice(idx + 1))
  writeQueue({ v: 1, batches, dropped: file.dropped })
  return true
}

/** How many events the cap has cost us since the last ACCEPTED report. */
export function peekDroppedCount(): number {
  return readQueue().dropped
}

/**
 * Forget `n` dropped events, because a `telemetry_queue_dropped` carrying them
 * was ACCEPTED by the server. Never clears more than it was told about, so a
 * drop that happened while the report was in flight is still reported next
 * time. #666 cleared the counter before the report was delivered, so a failed
 * report erased the only record that anything had been lost.
 */
export function clearDropped(n: number): void {
  if (!(n > 0)) return
  const file = readQueue()
  const dropped = Math.max(0, file.dropped - n)
  if (dropped === file.dropped) return
  writeQueue({ v: 1, batches: file.batches, dropped })
}

export function clearQueue(): void {
  const s = storage()
  try { s?.removeItem(QUEUE_KEY) } catch { /* nothing to do */ }
}
